import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SaveJobButton({ slug, className = "", showLabel = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (user && user.role === "candidate") {
      api.get("/candidate/saved-jobs").then(({ data }) => {
        if (active) setSaved(data.some((j) => j.slug === slug));
      }).catch(() => {});
    }
    return () => { active = false; };
  }, [user, slug]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || user.role !== "candidate") {
      toast.info("Please log in as a candidate to save jobs.");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/candidate/saved-jobs/${slug}`);
        setSaved(false);
        toast.success("Removed from saved jobs");
      } else {
        await api.post(`/candidate/saved-jobs/${slug}`);
        setSaved(true);
        toast.success("Job saved");
      }
    } catch (err) { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      data-testid={`save-job-${slug}`}
      onClick={toggle}
      disabled={busy}
      aria-label={saved ? "Unsave job" : "Save job"}
      className={`inline-flex items-center gap-2 transition-colors ${saved ? "text-brand-emerald" : "text-slate-400 hover:text-brand-emerald"} ${className}`}
    >
      {saved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
      {showLabel && <span className="text-sm font-medium">{saved ? "Saved" : "Save Job"}</span>}
    </button>
  );
}
